/**
 * Normalized API error.
 *
 * Every failed request in either app surfaces as one of these, so error
 * handling (toasts, form field errors, retry policy) is written once against a
 * single shape instead of against whatever each endpoint happened to return.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** Field-level errors, when the backend's ValidationPipe rejected the body. */
  readonly fieldErrors: Record<string, string[]> | undefined;
  readonly raw: unknown;

  constructor(args: {
    status: number;
    message: string;
    code?: string | undefined;
    fieldErrors?: Record<string, string[]> | undefined;
    raw?: unknown;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.fieldErrors = args.fieldErrors;
    this.raw = args.raw;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** 4xx that isn't auth-related — retrying will not help. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/** Thrown when the request never reached the server (offline, DNS, CORS, abort). */
export class NetworkError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('Network request failed. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Best-effort parse of the backend's error body into an ApiError.
 *
 * NOTE: this targets Nest's default exception shape (`{ statusCode, message,
 * error }`, where `message` is a string[] for validation failures). If the
 * backend's global exception filter in `libs/common` settles on a different
 * envelope, this function is the single place to update.
 */
export function toApiError(status: number, body: unknown): ApiError {
  if (body === null || typeof body !== 'object') {
    return new ApiError({ status, message: defaultMessageFor(status), raw: body });
  }

  const record = body as Record<string, unknown>;
  const rawMessage = record.message;

  // Nest's ValidationPipe returns message: string[] — one entry per failed rule.
  if (Array.isArray(rawMessage)) {
    const messages = rawMessage.filter((m): m is string => typeof m === 'string');
    return new ApiError({
      status,
      message: messages[0] ?? defaultMessageFor(status),
      code: typeof record.error === 'string' ? record.error : undefined,
      fieldErrors: groupValidationMessages(messages),
      raw: body,
    });
  }

  return new ApiError({
    status,
    message: typeof rawMessage === 'string' ? rawMessage : defaultMessageFor(status),
    code: typeof record.error === 'string' ? record.error : undefined,
    raw: body,
  });
}

/**
 * Normalizes an error returned by an auth client into an `ApiError`.
 *
 * The auth clients do their own fetching and never pass through
 * `createHttpClient`, so their failures arrive as a plain
 * `{ code, message, status }` object rather than as a thrown `ApiError` — a
 * second error shape that would otherwise leak into every sign-in, sign-up and
 * password-reset screen and force each one to handle both.
 *
 * This is shape normalization only: it imports nothing, knows nothing about
 * which auth library produced the object, and is safe for both apps. The auth
 * *clients* themselves stay per-app and must never be lifted into a package.
 *
 * `status` is often absent on these objects; `fallbackStatus` decides what a
 * failure with no status should be treated as.
 */
export function normalizeAuthError(error: unknown, fallbackStatus = 400): ApiError {
  if (error instanceof ApiError) return error;

  if (error === null || typeof error !== 'object') {
    return new ApiError({
      status: fallbackStatus,
      message: defaultMessageFor(fallbackStatus),
      raw: error,
    });
  }

  const record = error as Record<string, unknown>;
  const status = typeof record.status === 'number' ? record.status : fallbackStatus;

  // The code is the only stable identifier — messages are prose and change
  // between releases, so branch on `code`, never on `message`.
  const code =
    typeof record.code === 'string'
      ? record.code
      : typeof record.error === 'string'
        ? record.error
        : undefined;

  return new ApiError({
    status,
    message:
      typeof record.message === 'string' && record.message.length > 0
        ? record.message
        : defaultMessageFor(status),
    code,
    raw: error,
  });
}

/**
 * class-validator messages are prefixed with the property name
 * ("email must be an email"), which is enough to route them back to the right
 * form field. Anything unparseable lands under `_` so it is still shown.
 */
function groupValidationMessages(messages: string[]): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  for (const message of messages) {
    const field = message.split(' ')[0];
    const key = field && /^[a-zA-Z][\w.]*$/.test(field) ? field : '_';
    (grouped[key] ??= []).push(message);
  }

  return grouped;
}

function defaultMessageFor(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'Not found.';
  if (status === 429) return 'Too many requests. Please slow down and try again.';
  if (status >= 500) return 'Something went wrong on our end. Please try again.';
  return 'Request failed.';
}
